import { useEffect, useState } from "react";
import { Download, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/oraya-api";
import { StatusPill } from "@/admin/adminUi";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STATUSES = ["New", "Reviewing", "Shortlisted", "Rejected", "Hired"];

export default function AdminApplications() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/admin/applications").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/applications/${id}`, { status });
    toast.success("Status updated");
    setItems((arr) => arr.map((x) => (x.id === id ? { ...x, status } : x)));
    setSelected((s) => (s && s.id === id ? { ...s, status } : s));
  };

  const shown = filter === "All" ? items : items.filter((x) => x.status === filter);

  return (
    <div>
      <h1 className="display text-3xl font-light text-oraya-mist">Applications</h1>
      <p className="text-oraya-slate mt-1">Candidates who applied to open roles.</p>

      <div className="flex flex-wrap gap-2 mt-6 mb-6">
        {["All", ...STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`app-filter-${s.toLowerCase()}`}
            className={`px-3 py-1.5 rounded-full text-xs transition-colors ${filter === s ? "bg-oraya-mist text-oraya-black" : "glass-medium text-oraya-slate hover:text-oraya-mist"}`}>{s}</button>
        ))}
      </div>

      <div className="bg-oraya-graphite border border-white/[0.06] rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-oraya-slate border-b border-white/[0.06]">
            <th className="px-6 py-4 font-medium">Candidate</th><th className="px-6 py-4 font-medium hidden md:table-cell">Role</th>
            <th className="px-6 py-4 font-medium">Status</th><th className="px-6 py-4 font-medium text-right">Manage</th></tr></thead>
          <tbody>
            {shown.map((a) => (
              <tr key={a.id} className="border-b border-white/[0.04] text-oraya-mist hover:bg-white/[0.02] cursor-pointer" onClick={() => setSelected(a)} data-testid={`application-row-${a.id}`}>
                <td className="px-6 py-4"><div className="font-medium">{a.name}</div><div className="text-oraya-slate text-xs">{a.email}</div></td>
                <td className="px-6 py-4 hidden md:table-cell text-oraya-slate">{a.job_title}</td>
                <td className="px-6 py-4"><StatusPill status={a.status} /></td>
                <td className="px-6 py-4 text-right" onClick={(ev) => ev.stopPropagation()}>
                  <select value={a.status} onChange={(ev) => setStatus(a.id, ev.target.value)} data-testid={`application-status-${a.id}`} className="bg-oraya-black border border-white/10 rounded-lg px-2 py-1.5 text-xs text-oraya-mist focus:border-oraya-accent focus:outline-none">
                    {STATUSES.map((s) => <option key={s} value={s} className="bg-oraya-graphite">{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
            {shown.length === 0 && <tr><td colSpan="4" className="px-6 py-10 text-center text-oraya-slate">No applications.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setSelected(null)} data-testid="application-detail">
          <div className="bg-oraya-graphite border border-white/10 rounded-2xl w-full max-w-lg p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <StatusPill status={selected.status} /><span className="text-xs text-oraya-slate">{selected.id}</span>
            </div>
            <h3 className="display text-2xl font-medium text-oraya-mist mt-4">{selected.name}</h3>
            <p className="text-oraya-slate text-sm">{selected.job_title}</p>
            <div className="mt-4 space-y-2 text-sm text-oraya-slate">
              <p><span className="text-oraya-mist">Email:</span> {selected.email}</p>
              {selected.phone && <p><span className="text-oraya-mist">Phone:</span> {selected.phone}</p>}
              {selected.linkedin && <p><span className="text-oraya-mist">LinkedIn:</span> <a href={selected.linkedin} target="_blank" rel="noreferrer" className="text-oraya-accent inline-flex items-center gap-1">link <ExternalLink className="w-3 h-3" /></a></p>}
              {selected.portfolio && <p><span className="text-oraya-mist">Portfolio:</span> <a href={selected.portfolio} target="_blank" rel="noreferrer" className="text-oraya-accent inline-flex items-center gap-1">link <ExternalLink className="w-3 h-3" /></a></p>}
            </div>
            {selected.cover_note && <p className="mt-4 text-oraya-mist/90 leading-relaxed whitespace-pre-line border-t border-white/[0.06] pt-4">{selected.cover_note}</p>}
            <a href={`${BACKEND_URL}/api/admin/applications/${selected.id}/resume`} target="_blank" rel="noreferrer" data-testid={`download-resume-${selected.id}`} className="mt-6 inline-flex items-center gap-2 rounded-lg bg-oraya-mist text-oraya-black px-4 py-2.5 text-sm font-medium hover:bg-white">
              <Download className="w-4 h-4" /> View résumé
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
